/**
 * I WANT IT — demand signal on items nobody is selling.
 *
 * THE ONE RULE THIS FILE EXISTS TO PROTECT:
 *   The IWI event is recorded FIRST and ALWAYS, separately from any attempt to
 *   deliver it. Blocked, rate-limited, declined and unanswered pings are all
 *   recorded. Fourteen IWIs on a not-for-sale item is the most valuable row in
 *   the table, and it only exists if the record is independent of delivery.
 *
 * Phase 0 (this build) records demand and delivers nothing. Every event is
 * written with deliveryState 'not_attempted' and stays there. The rest of the
 * state machine is defined here anyway so Phase 1 extends it rather than
 * redefining it — and so `no_response` exists from the start.
 */

export type IwiKind =
  | 'ping'      // open a conversation about selling
  | 'bounty';   // standing offer anyone can fulfil (Phase 2 — schema ready, no UI)

/**
 * What happened to the ATTEMPT TO DELIVER this event. Never conflate with
 * whether the event was recorded — if it exists, it was recorded.
 *
 * 'no_response' is load-bearing. Silence is the most common outcome of asking a
 * stranger to sell something, and without a terminal state for it every
 * unanswered ping sits in 'sent' forever: the requester never learns anything
 * and the usual result of the system is nothing happening quietly. A timeout is
 * what lets the requester move on, and "fourteen pings, zero responses" is
 * demand data in its own right.
 */
export type IwiDeliveryState =
  | 'not_attempted'   // Phase 0: recorded, nothing sent. The only state we write today.
  | 'queued'          // accepted for relay, not yet sent
  | 'sent'            // relayed to the last known owner
  | 'blocked_no_contact'  // owner state is 'no contact' — recorded, deliberately not sent
  | 'rate_limited'    // suppressed to protect the owner relationship — still recorded
  | 'declined'        // owner said no
  | 'accepted'        // owner engaged
  | 'no_response';    // timed out after the response window. A real outcome, not a gap.

/** Days of silence after which a relayed ping becomes 'no_response'. Phase 1. */
export const IWI_RESPONSE_WINDOW_DAYS = 14;

/**
 * How an item is identified.
 *
 * Both keys are stored, always. The TTR code survives resale; the lot is where
 * the item WAS, not what it IS. Today whitelabelbuddy lots carry no TTR code
 * (AuctionLot has `sku`, not a ttrCode), so lot-originated events store
 * ttrCode: null and are keyed by auction+lot. Verify-page events carry the code.
 */
export interface IwiItemRef {
  /** TTR code when known — the identity that outlives this sale. */
  ttrCode: string | null;
  /** Where it was: the sale and lot it last passed through. Always recorded. */
  auctionId: string | null;
  lotId: string | null;
  /** Denormalised so an event stays readable if a sale is ever restructured. */
  lotNumber: string | null;
  sku: string | null;
  itemTitle: string | null;
}

export interface IwiEvent {
  /** Stable join key: the TTR code when there is one, else `${auctionId}_${lotId}`. */
  itemKey: string;
  item: IwiItemRef;
  kind: IwiKind;
  /** What they'd pay, in CENTS (the unit used everywhere in this codebase). Optional. */
  amountCents: number | null;
  /** Short free-text note from the requester. Capped. Optional. */
  note: string | null;

  /** Signed-in requester. Null for anonymous. Rules enforce it matches auth.uid. */
  requesterUid: string | null;
  /** Anonymous requester's contact. Null when signed in. */
  requesterEmail: string | null;

  /**
   * The sale this item last changed hands in, and WHEN. Recorded on the event so
   * the age of the ownership claim is preserved at the moment of asking — never
   * recomputed later, never presented as "the owner".
   */
  lastKnownSaleId: string | null;
  lastKnownSaleTitle: string | null;
  lastKnownSaleEndedAt: Date | null;

  deliveryState: IwiDeliveryState;
  source: 'past-auction-lot' | 'sku-verify';
  createdAt: unknown;   // serverTimestamp() sentinel on write
}

export const IWI_NOTE_MAX = 500;
